/**
 * Building-wide stage modal — single-record editor for BUILDING/PROJECT-scoped
 * WBS stages (Plinth, Lifts, Water Pumps, Handover, …).
 *
 * One building has exactly one record per such stage (floor IS NULL on the
 * wire), so instead of the per-floor add-row screen this is a simple modal:
 * Target Date + Achieved Date + completion image. Creates the construction
 * event on first save, updates it afterwards; image uploads via the multipart
 * companion endpoint after the event exists.
 *
 * Uses native <input type="date"> — popper-based pickers clip/stack badly
 * inside modals. Achieved date is capped at today (no future achievements).
 */
import React, { useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { X, Eye } from "lucide-react";
import { OutlineButton, PrimaryButton } from "../Buttons";
import {
  createConstructionEvent,
  updateConstructionEvent,
  uploadConstructionEventMedia,
} from "../../services/wbs/index";

function BuildingStageModal({ stage, buildingId, buildingName, event, onClose, onSaved }) {
  const [targetStartDate, setTargetStartDate] = useState(event?.targetStartDate || "");
  const [plannedDate, setPlannedDate] = useState(event?.plannedDate || "");
  const [actualStartDate, setActualStartDate] = useState(event?.actualStartDate || "");
  const [achievedDate, setAchievedDate] = useState(event?.achievedDate || "");
  const [imageFile, setImageFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const existingImageUrl = event?.imageUrl || null;
  const today = moment().format("YYYY-MM-DD");

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (targetStartDate && plannedDate && plannedDate < targetStartDate) {
        setIsSaving(false);
        return toast.error("Target End cannot be before Target Start.");
      }
      if (actualStartDate && achievedDate && achievedDate < actualStartDate) {
        setIsSaving(false);
        return toast.error("Achieved date cannot be before Actual Start.");
      }
      const body = {
        buildingId,
        wbsStageId: stage.wbsStageId,
        floor: null,
        targetStartDate: targetStartDate || null,
        plannedDate: plannedDate || null,
        actualStartDate: actualStartDate || null,
        achievedDate: achievedDate || null,
      };

      let savedEvent;
      if (event?.constructionEventId) {
        savedEvent = await updateConstructionEvent({ ...body, constructionEventId: event.constructionEventId });
      } else {
        savedEvent = await createConstructionEvent(body);
      }

      const eventId = savedEvent?.constructionEventId || event?.constructionEventId;
      if (imageFile instanceof File && eventId) {
        try {
          await uploadConstructionEventMedia(eventId, imageFile);
        } catch (mediaErr) {
          toast.error("Status saved, but the photo upload failed — reopen to retry.");
          console.error("Error uploading progress image:", mediaErr);
        }
      }

      toast.success("Status saved successfully");
      if (onSaved) onSaved();
      onClose();
    } catch (error) {
      console.error("Error saving building stage status:", error);
      if (error?.response?.status === 409) {
        toast.error("A record for this building/stage already exists.");
      } else {
        toast.error("Error saving status.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="relative bg-white rounded-lg shadow-xl w-[440px] max-w-[95vw] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{stage.label}</h3>
            <p className="text-sm text-gray-500">{buildingName} · building-wide</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* v3_32: targets and actuals are start/end pairs; achieved (end) drives % */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Target Start</label>
              <input
                type="date"
                value={targetStartDate}
                onChange={(e) => setTargetStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Target End</label>
              <input
                type="date"
                value={plannedDate}
                onChange={(e) => setPlannedDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Actual Start</label>
              <input
                type="date"
                value={actualStartDate}
                max={today}
                onChange={(e) => setActualStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Achieved Date</label>
              <input
                type="date"
                value={achievedDate}
                max={today}
                onChange={(e) => setAchievedDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-400">Drives completion % · not in the future.</p>
            </div>
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Completion Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files[0] || null)}
              className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {imageFile ? (
              <p className="text-xs text-gray-500">{imageFile.name}</p>
            ) : existingImageUrl ? (
              <button
                onClick={() => setPreviewUrl(existingImageUrl)}
                className="inline-flex items-center gap-1.5 mt-1 px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <Eye size={14} />
                View current image
              </button>
            ) : (
              <p className="text-xs text-gray-400">No image uploaded yet</p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
          <OutlineButton className="px-4 py-2 text-sm" onClick={onClose}>
            Cancel
          </OutlineButton>
          <PrimaryButton className="px-4 py-2 text-sm" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving…" : "Save"}
          </PrimaryButton>
        </div>
      </div>

      {previewUrl && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70]"
          onClick={() => setPreviewUrl(null)}
        >
          <img
            src={previewUrl}
            alt={`${stage.label} completion`}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
          />
        </div>
      )}
    </div>
  );
}

export default BuildingStageModal;
